import { generateIfcGuid, stepString, stepFloat, stepEnum, stepList, stepCompoundAngle, STEP_UNDEFINED, STEP_DERIVED } from './ifc-utils';
import { IfcOpeningVoid, IfcPoint, IfcSiteGeoRef } from './types';

type P2 = { x: number; y: number };

/** Void over-shoot past each wall face, so the boolean cut fully penetrates. */
const VOID_OVERSHOOT = 0.05;
/** Door leaf / window pane thickness. */
const FILLING_THICKNESS = 0.05;
/**
 * When true, each typed opening also gets an IfcDoor/IfcWindow filling placed
 * back in the hole (IfcRelFillsElement). When false, only the void is cut — the
 * wall shows an empty opening and no door/window object is emitted. Disabled for
 * now; flip back to re-enable filling objects (the full machinery is retained).
 */
export const EMIT_OPENING_FILLINGS = false as boolean;

/** Extend a 4-corner opening rect outward along the through-wall axis by `d`. */
function overshootRect(corners: P2[], d: number): P2[] {
  if (corners.length !== 4) return corners;
  const [c0, c1, c2, c3] = corners;
  const ax = (c2.x + c3.x) / 2 - (c0.x + c1.x) / 2;
  const ay = (c2.y + c3.y) / 2 - (c0.y + c1.y) / 2;
  const len = Math.hypot(ax, ay) || 1;
  const nx = (ax / len) * d;
  const ny = (ay / len) * d;
  return [
    { x: c0.x - nx, y: c0.y - ny },
    { x: c1.x - nx, y: c1.y - ny },
    { x: c2.x + nx, y: c2.y + ny },
    { x: c3.x + nx, y: c3.y + ny },
  ];
}

/** Thin rect centred in the wall spanning the opening width (door leaf / window pane), plus that width. */
function thinPane(corners: P2[], thickness: number): { rect: P2[]; width: number } {
  if (corners.length !== 4) return { rect: corners, width: 0 };
  const [c0, c1, c2, c3] = corners;
  const a = { x: (c0.x + c3.x) / 2, y: (c0.y + c3.y) / 2 }; // jamb-A centreline
  const b = { x: (c1.x + c2.x) / 2, y: (c1.y + c2.y) / 2 }; // jamb-B centreline
  const ax = (c2.x + c3.x) / 2 - (c0.x + c1.x) / 2;
  const ay = (c2.y + c3.y) / 2 - (c0.y + c1.y) / 2;
  const len = Math.hypot(ax, ay) || 1;
  const half = thickness / 2;
  const nx = (ax / len) * half;
  const ny = (ay / len) * half;
  return {
    rect: [
      { x: a.x - nx, y: a.y - ny },
      { x: b.x - nx, y: b.y - ny },
      { x: b.x + nx, y: b.y + ny },
      { x: a.x + nx, y: a.y + ny },
    ],
    width: Math.hypot(b.x - a.x, b.y - a.y),
  };
}

/**
 * References to boilerplate project hierarchy entities,
 * returned by writeProjectHierarchy() for use by wall writers.
 */
export interface ProjectRefs {
  geometricContext: string;
  /** 'Axis' sub-context — wall reference-line (Curve2D) representations attach here. */
  axisContext: string;
  bodyContext: string;
  ownerHistory: string;
  building: string;
  /** IfcSite reference — georeferencing property sets attach here. */
  site: string;
  /** Building-relative placement, kept at the origin so geometry stays in small local coords (Revit-safe). Walls nest under it. */
  objectPlacement: string;
  /** Identity IfcAxis2Placement3D reused as the RelativePlacement of every nested IfcLocalPlacement. */
  identityAxis: string;
  extrusionDirection: string;
}

/**
 * Low-level IFC STEP (ISO 10303-21) writer.
 *
 * Generates valid IFC2X3 files following the same entity structure
 * as the C++ export_IFC.cpp implementation. Manages entity IDs and
 * accumulates STEP lines for final output.
 */
export class IfcWriter {
  private nextEntityId = 1;
  private lines: string[] = [];
  // Shared surface styles for door/window fillings, created lazily on first use.
  private doorStyleRef: string | null = null;
  private windowStyleRef: string | null = null;
  // Shared wall material (for IfcMaterialLayerSet), created lazily on first use.
  private wallMaterialRef: string | null = null;
  // One IfcMaterialLayerSet per wall thickness, so importers (Revit) derive one
  // sensibly-named wall type per thickness instead of a type per wall.
  private wallLayerSetByThickness = new Map<string, string>();

  /** A nested IfcLocalPlacement at identity (no transform) relative to `parent`. */
  private localPlacement(parent: string, identityAxis: string): string {
    return this.addEntity('IFCLOCALPLACEMENT', `${parent},${identityAxis}`);
  }

  private nextId(): string {
    const id = `#${this.nextEntityId}`;
    this.nextEntityId++;
    return id;
  }

  private addLine(line: string): void {
    this.lines.push(line);
  }

  /**
   * Add an entity to the DATA section.
   * Returns the entity reference (e.g. "#5").
   */
  addEntity(type: string, args: string): string {
    const id = this.nextId();
    this.addLine(`${id}= ${type}(${args});`);
    return id;
  }

  /**
   * Write the ISO-10303-21 HEADER section.
   */
  writeHeader(fileName: string): void {
    const timestamp = new Date().toISOString().replace(/\.\d+Z$/, '');

    this.addLine('ISO-10303-21;');
    this.addLine('HEADER;');
    this.addLine(`FILE_DESCRIPTION(('ViewDefinition [CoordinationView_V2.0]'), '2;1');`);
    this.addLine('FILE_NAME(');
    this.addLine(`/* name */ ${stepString(fileName)},`);
    this.addLine(`/* time_stamp */ ${stepString(timestamp)},`);
    this.addLine(`/* author */ ('Pointorama'),`);
    this.addLine(`/* organization */ (),`);
    this.addLine(`/* preprocessor_version */ '',`);
    this.addLine(`/* originating_system */ '',`);
    this.addLine(`/* authorization */ 'None');`);
    this.addLine('');
    this.addLine(`FILE_SCHEMA(('IFC2X3'));`);
    this.addLine('ENDSEC;');
    this.addLine('');
  }

  /**
   * Write the DATA section opening and project hierarchy.
   * Returns entity references needed for creating walls.
   */
  writeProjectHierarchy(projectName: string, georef?: IfcSiteGeoRef): ProjectRefs {
    this.addLine('DATA;');

    // Geometric representation context (matches C++ #1-#8)
    const originPoint = this.addEntity('IFCCARTESIANPOINT', `(${stepFloat(0)},${stepFloat(0)},${stepFloat(0)})`);
    const originPlacement = this.addEntity('IFCAXIS2PLACEMENT3D', `${originPoint},${STEP_UNDEFINED},${STEP_UNDEFINED}`);
    const geometricContext = this.addEntity('IFCGEOMETRICREPRESENTATIONCONTEXT', `${STEP_UNDEFINED},${stepString('Model')},3,${stepFloat(0.0001)},${originPlacement},${STEP_UNDEFINED}`);
    const axisContext = this.addEntity(
      'IFCGEOMETRICREPRESENTATIONSUBCONTEXT',
      `${stepString('Axis')},${stepString('Model')},${STEP_DERIVED},${STEP_DERIVED},${STEP_DERIVED},${STEP_DERIVED},${geometricContext},${STEP_UNDEFINED},${stepEnum('MODEL_VIEW')},${STEP_UNDEFINED}`,
    );
    const bodyContext = this.addEntity(
      'IFCGEOMETRICREPRESENTATIONSUBCONTEXT',
      `${stepString('Body')},${stepString('Model')},${STEP_DERIVED},${STEP_DERIVED},${STEP_DERIVED},${STEP_DERIVED},${geometricContext},${STEP_UNDEFINED},${stepEnum('MODEL_VIEW')},${STEP_UNDEFINED}`,
    );
    // Placement chain stays at the origin (0,0,0). The geometry is already shifted
    // to small, local coordinates; the world offset is NOT re-applied here as a
    // placement translation — doing so would push the model thousands of km from
    // the origin and break tools (e.g. Revit) that honor placements. The model's
    // real-world location is carried by the IfcSite RefLatitude/RefLongitude below.
    const sitePlacement = this.addEntity('IFCLOCALPLACEMENT', `${STEP_UNDEFINED},${originPlacement}`);
    const buildingPlacement = this.addEntity('IFCLOCALPLACEMENT', `${sitePlacement},${originPlacement}`);
    const extrusionDirection = this.addEntity('IFCDIRECTION', `(${stepFloat(0)},${stepFloat(0)},${stepFloat(1)})`);

    // Units
    const lengthUnit = this.addEntity('IFCSIUNIT', `${STEP_DERIVED},${stepEnum('LENGTHUNIT')},${STEP_UNDEFINED},${stepEnum('METRE')}`);
    const angleUnit = this.addEntity('IFCSIUNIT', `${STEP_DERIVED},${stepEnum('PLANEANGLEUNIT')},${STEP_UNDEFINED},${stepEnum('RADIAN')}`);
    const timeUnit = this.addEntity('IFCSIUNIT', `${STEP_DERIVED},${stepEnum('TIMEUNIT')},${STEP_UNDEFINED},${stepEnum('SECOND')}`);
    const unitAssignment = this.addEntity('IFCUNITASSIGNMENT', stepList([lengthUnit, angleUnit, timeUnit]));

    // Project
    const project = this.addEntity(
      'IFCPROJECT',
      `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString(projectName)},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${stepList([geometricContext])},${unitAssignment}`,
    );

    // Site — anchors the model geographically (RefLatitude/RefLongitude/RefElevation)
    // and carries the world origin as its placement.
    const refLat = georef?.refLatitude != null ? stepCompoundAngle(georef.refLatitude) : STEP_UNDEFINED;
    const refLong = georef?.refLongitude != null ? stepCompoundAngle(georef.refLongitude) : STEP_UNDEFINED;
    const refElev = georef?.refElevation != null ? stepFloat(georef.refElevation) : STEP_UNDEFINED;
    const site = this.addEntity(
      'IFCSITE',
      `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString(georef?.name ?? 'Ifc Site')},${STEP_UNDEFINED},${STEP_UNDEFINED},${sitePlacement},${STEP_UNDEFINED},${STEP_UNDEFINED},${stepEnum('ELEMENT')},${refLat},${refLong},${refElev},${STEP_UNDEFINED},${STEP_UNDEFINED}`,
    );

    // Building (placed against the site origin).
    const building = this.addEntity(
      'IFCBUILDING',
      `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString('Ifc Building')},${STEP_UNDEFINED},${STEP_UNDEFINED},${buildingPlacement},${STEP_UNDEFINED},${STEP_UNDEFINED},${stepEnum('ELEMENT')},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED}`,
    );

    // Project -> Site -> Building aggregation
    this.addEntity('IFCRELAGGREGATES', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${project},${stepList([site])}`);
    this.addEntity('IFCRELAGGREGATES', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${site},${stepList([building])}`);

    return {
      geometricContext,
      axisContext,
      bodyContext,
      ownerHistory: STEP_UNDEFINED,
      building,
      site,
      objectPlacement: buildingPlacement,
      identityAxis: originPlacement,
      extrusionDirection,
    };
  }

  /**
   * Record georeferencing on the IfcSite without moving geometry: the offset that
   * was subtracted from every coordinate is written as ePset_MapConversion
   * (Eastings/Northings/OrthogonalHeight), so `world = local + offset` is
   * recoverable, and the CRS as ePset_ProjectedCRS. This is the bSI-recognized way
   * to carry IFC4 map-conversion data inside an IFC2x3 file.
   */
  writeSiteGeoreferencing(site: string, origin: IfcPoint, crsName?: string): void {
    const prop = (name: string, value: string) => this.addEntity('IFCPROPERTYSINGLEVALUE', `${stepString(name)},${STEP_UNDEFINED},${value},${STEP_UNDEFINED}`);
    const attach = (pset: string) => this.addEntity('IFCRELDEFINESBYPROPERTIES', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${stepList([site])},${pset}`);

    const mapProps = [prop('Eastings', `IFCREAL(${stepFloat(origin.x)})`), prop('Northings', `IFCREAL(${stepFloat(origin.y)})`), prop('OrthogonalHeight', `IFCREAL(${stepFloat(origin.z)})`)];
    const mapPset = this.addEntity('IFCPROPERTYSET', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString('ePset_MapConversion')},${STEP_UNDEFINED},${stepList(mapProps)}`);
    attach(mapPset);

    if (crsName) {
      const crsPset = this.addEntity('IFCPROPERTYSET', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString('ePset_ProjectedCRS')},${STEP_UNDEFINED},${stepList([prop('Name', `IFCLABEL(${stepString(crsName)})`)])}`);
      attach(crsPset);
    }
  }

  /**
   * Write an additional IfcBuildingStorey (one per floor) aggregated under the
   * building. Returns the storey entity reference for spatial containment.
   */
  writeStorey(name: string, elevation: number, building: string): string {
    const storey = this.addEntity(
      'IFCBUILDINGSTOREY',
      `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString(name)},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${stepEnum('ELEMENT')},${stepFloat(elevation)}`,
    );
    this.addEntity('IFCRELAGGREGATES', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${building},${stepList([storey])}`);
    return storey;
  }

  /**
   * Write an IfcSpace (room) from a footprint extruded floor→ceiling, placed
   * under the building at identity (geometry already in world-local coords).
   * The space is added to the spatial structure by the caller via
   * {@link writeSpaceAggregation}; walls are bonded via {@link writeSpaceBoundary}.
   */
  writeSpace(number: string, longName: string, footprint: P2[], elevation: number, height: number, refs: ProjectRefs): string {
    const placement = this.localPlacement(refs.objectPlacement, refs.identityAxis);
    const prodShape = this.writeExtrudedProdShape(footprint, elevation, height, refs);
    // Name carries a unique room number, LongName the display name — the IFC
    // convention Revit maps to Room Number/Name (duplicate Names trip its
    // "duplicate Number" warning).
    // IfcSpace attrs: …,ObjectPlacement,Representation,LongName,CompositionType,InteriorOrExteriorSpace,ElevationWithFlooring.
    return this.addEntity(
      'IFCSPACE',
      `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString(number)},${STEP_UNDEFINED},${STEP_UNDEFINED},${placement},${prodShape},${stepString(longName)},${stepEnum('ELEMENT')},${stepEnum('INTERNAL')},${STEP_UNDEFINED}`,
    );
  }

  /** Aggregate spaces under their storey (IfcRelAggregates) — the spatial-structure decomposition. */
  writeSpaceAggregation(storeyRef: string, spaceRefs: string[]): void {
    if (spaceRefs.length === 0) return;
    this.addEntity('IFCRELAGGREGATES', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${storeyRef},${stepList(spaceRefs)}`);
  }

  /**
   * Bond a wall to a space it bounds (IfcRelSpaceBoundary). Physical boundary,
   * internal/external left undefined; connection geometry omitted (the wall's own
   * body suffices for tools that compute room areas).
   */
  writeSpaceBoundary(space: string, wall: string): void {
    this.addEntity(
      'IFCRELSPACEBOUNDARY',
      `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${space},${wall},${STEP_UNDEFINED},${stepEnum('PHYSICAL')},${stepEnum('NOTDEFINED')}`,
    );
  }

  /**
   * Write a standard-case wall from a centered cross-section quad plus its axis
   * line. Emits both an 'Axis' (reference line) and a 'Body' (SweptSolid)
   * representation, an IfcWallStandardCase, and a single-layer
   * IfcMaterialLayerSetUsage centred on the axis (thickness = `thickness`), so
   * BIM tools import it as a real parametric wall. Openings are cut afterwards.
   *
   * @param name Wall name
   * @param quadPoints 4 corner points of the centered cross-section (2D, XY plane)
   * @param axisStart Wall axis start (2D) — the centerline the thickness is centred on
   * @param axisEnd Wall axis end (2D)
   * @param elevation Z elevation of the wall base
   * @param height Extrusion height
   * @param thickness Total wall thickness (centred on the axis)
   * @param refs Project hierarchy references
   * @param openings Door/window voids to cut from the wall
   */
  writeWallStandardCase(
    name: string,
    quadPoints: Array<{ x: number; y: number }>,
    axisStart: { x: number; y: number },
    axisEnd: { x: number; y: number },
    elevation: number,
    height: number,
    thickness: number,
    refs: ProjectRefs,
    openings?: IfcOpeningVoid[],
  ): { wall: string; fillings: string[] } {
    // Body: centered quad profile → extruded solid → SweptSolid representation.
    const polyCurve = this.writeClosedPolyCurve(quadPoints);
    const profile = this.addEntity('IFCARBITRARYCLOSEDPROFILEDEF', `${stepEnum('AREA')},${STEP_UNDEFINED},${polyCurve}`);
    const placementPoint = this.addEntity('IFCCARTESIANPOINT', `(${stepFloat(0)},${stepFloat(0)},${stepFloat(elevation)})`);
    const placementAxis = this.addEntity('IFCAXIS2PLACEMENT3D', `${placementPoint},${STEP_UNDEFINED},${STEP_UNDEFINED}`);
    const solid = this.addEntity('IFCEXTRUDEDAREASOLID', `${profile},${placementAxis},${refs.extrusionDirection},${stepFloat(height)}`);
    const bodyRep = this.addEntity('IFCSHAPEREPRESENTATION', `${refs.bodyContext},${stepString('Body')},${stepString('SweptSolid')},${stepList([solid])}`);

    // Axis: 2D reference line through the wall centerline.
    const axisCurve = this.addEntity(
      'IFCPOLYLINE',
      stepList([this.addEntity('IFCCARTESIANPOINT', `(${stepFloat(axisStart.x)},${stepFloat(axisStart.y)})`), this.addEntity('IFCCARTESIANPOINT', `(${stepFloat(axisEnd.x)},${stepFloat(axisEnd.y)})`)]),
    );
    const axisRep = this.addEntity('IFCSHAPEREPRESENTATION', `${refs.axisContext},${stepString('Axis')},${stepString('Curve2D')},${stepList([axisCurve])}`);

    const prodShape = this.addEntity('IFCPRODUCTDEFINITIONSHAPE', `${STEP_UNDEFINED},${STEP_UNDEFINED},${stepList([axisRep, bodyRep])}`);

    // Per-wall placement nested under the building, so openings can nest under it.
    const wallPlacement = this.localPlacement(refs.objectPlacement, refs.identityAxis);
    const wall = this.addEntity('IFCWALLSTANDARDCASE', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString(name)},${STEP_UNDEFINED},${STEP_UNDEFINED},${wallPlacement},${prodShape},${STEP_UNDEFINED}`);

    // Material: a single layer of the full thickness, centred on the axis.
    const layerSet = this.getWallLayerSet(thickness);
    const layerSetUsage = this.addEntity('IFCMATERIALLAYERSETUSAGE', `${layerSet},${stepEnum('AXIS2')},${stepEnum('POSITIVE')},${stepFloat(-thickness / 2)}`);
    this.addEntity('IFCRELASSOCIATESMATERIAL', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${stepList([wall])},${layerSetUsage}`);

    const fillings = this.cutOpenings(wall, wallPlacement, openings, name, refs);
    return { wall, fillings };
  }

  /**
   * Create an IfcPolyline from an array of 2D points (closed loop).
   * Emits one IfcCartesianPoint per point and repeats the first point to close
   * the loop explicitly. Returns the polyline entity reference.
   */
  private writeClosedPolyCurve(points: Array<{ x: number; y: number }>): string {
    const pointRefs = points.map((p) => this.addEntity('IFCCARTESIANPOINT', `(${stepFloat(p.x)},${stepFloat(p.y)})`));
    return this.addEntity('IFCPOLYLINE', stepList([...pointRefs, pointRefs[0]]));
  }

  /**
   * Write a wall entity from outline loops (outer boundary + optional holes).
   * Creates an extruded solid from the outline profile.
   * Returns the wall entity reference.
   *
   * @param name Wall name
   * @param outerLoop Outer boundary polygon points (2D)
   * @param innerLoops Optional inner boundary polygons (holes)
   * @param elevation Z elevation of the wall base
   * @param height Extrusion height
   * @param refs Project hierarchy references
   */
  writeWallFromOutline(
    name: string,
    outerLoop: Array<{ x: number; y: number }>,
    innerLoops: Array<Array<{ x: number; y: number }>> | undefined,
    elevation: number,
    height: number,
    refs: ProjectRefs,
    openings?: IfcOpeningVoid[],
  ): { wall: string; fillings: string[] } {
    const outerCurve = this.writeClosedPolyCurve(outerLoop);

    let profile: string;
    if (innerLoops && innerLoops.length > 0) {
      // IfcArbitraryProfileDefWithVoids -- outer profile + inner holes
      const innerCurves = innerLoops.map((loop) => this.writeClosedPolyCurve(loop));
      profile = this.addEntity('IFCARBITRARYPROFILEDEFWITHVOIDS', `${stepEnum('AREA')},${STEP_UNDEFINED},${outerCurve},${stepList(innerCurves)}`);
    } else {
      // IfcArbitraryClosedProfileDef -- no holes
      profile = this.addEntity('IFCARBITRARYCLOSEDPROFILEDEF', `${stepEnum('AREA')},${STEP_UNDEFINED},${outerCurve}`);
    }

    // Placement at elevation
    const placementPoint = this.addEntity('IFCCARTESIANPOINT', `(${stepFloat(0)},${stepFloat(0)},${stepFloat(elevation)})`);
    const placementAxis = this.addEntity('IFCAXIS2PLACEMENT3D', `${placementPoint},${STEP_UNDEFINED},${STEP_UNDEFINED}`);

    // IfcExtrudedAreaSolid
    const solid = this.addEntity('IFCEXTRUDEDAREASOLID', `${profile},${placementAxis},${refs.extrusionDirection},${stepFloat(height)}`);

    // IfcShapeRepresentation
    const shapeRep = this.addEntity('IFCSHAPEREPRESENTATION', `${refs.bodyContext},${stepString('Body')},${stepString('SweptSolid')},${stepList([solid])}`);

    // IfcProductDefinitionShape
    const prodShape = this.addEntity('IFCPRODUCTDEFINITIONSHAPE', `${STEP_UNDEFINED},${STEP_UNDEFINED},${stepList([shapeRep])}`);

    // IfcWall — per-wall placement nested under the building, so openings nest under it.
    const wallPlacement = this.localPlacement(refs.objectPlacement, refs.identityAxis);
    const wall = this.addEntity('IFCWALL', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString(name)},${STEP_UNDEFINED},${STEP_UNDEFINED},${wallPlacement},${prodShape},${STEP_UNDEFINED}`);

    const fillings = this.cutOpenings(wall, wallPlacement, openings, name, refs);

    return { wall, fillings };
  }

  /** Extruded solid from a closed footprint → IfcProductDefinitionShape; styled via IfcStyledItem when `styleRef` is given. */
  private writeExtrudedProdShape(footprint: P2[], elevation: number, height: number, refs: ProjectRefs, styleRef?: string): string {
    const curve = this.writeClosedPolyCurve(footprint);
    const profile = this.addEntity('IFCARBITRARYCLOSEDPROFILEDEF', `${stepEnum('AREA')},${STEP_UNDEFINED},${curve}`);
    const point = this.addEntity('IFCCARTESIANPOINT', `(${stepFloat(0)},${stepFloat(0)},${stepFloat(elevation)})`);
    const axis = this.addEntity('IFCAXIS2PLACEMENT3D', `${point},${STEP_UNDEFINED},${STEP_UNDEFINED}`);
    const solid = this.addEntity('IFCEXTRUDEDAREASOLID', `${profile},${axis},${refs.extrusionDirection},${stepFloat(height)}`);
    if (styleRef) this.addEntity('IFCSTYLEDITEM', `${solid},${stepList([styleRef])},${STEP_UNDEFINED}`);
    const shapeRep = this.addEntity('IFCSHAPEREPRESENTATION', `${refs.bodyContext},${stepString('Body')},${stepString('SweptSolid')},${stepList([solid])}`);
    return this.addEntity('IFCPRODUCTDEFINITIONSHAPE', `${STEP_UNDEFINED},${STEP_UNDEFINED},${stepList([shapeRep])}`);
  }

  /**
   * Shared opaque surface style; RGB in 0–1. IFC2X3 IfcSurfaceStyleShading has no
   * transparency, so the style is wrapped in an IfcPresentationStyleAssignment —
   * the only form IfcStyledItem.Styles accepts in IFC2X3.
   */
  private surfaceStyle(name: string, r: number, g: number, b: number): string {
    const colour = this.addEntity('IFCCOLOURRGB', `${STEP_UNDEFINED},${stepFloat(r)},${stepFloat(g)},${stepFloat(b)}`);
    const shading = this.addEntity('IFCSURFACESTYLESHADING', `${colour}`);
    const style = this.addEntity('IFCSURFACESTYLE', `${stepString(name)},${stepEnum('BOTH')},${stepList([shading])}`);
    return this.addEntity('IFCPRESENTATIONSTYLEASSIGNMENT', stepList([style]));
  }

  private getDoorStyle(): string {
    if (!this.doorStyleRef) this.doorStyleRef = this.surfaceStyle('Door', 0.55, 0.35, 0.18); // solid wood brown
    return this.doorStyleRef;
  }

  private getWindowStyle(): string {
    if (!this.windowStyleRef) this.windowStyleRef = this.surfaceStyle('Glazing', 0.6, 0.8, 0.95); // opaque glass blue
    return this.windowStyleRef;
  }

  /** Shared IfcMaterial used by every standard-case wall's layer set. */
  private getWallMaterial(): string {
    if (!this.wallMaterialRef) this.wallMaterialRef = this.addEntity('IFCMATERIAL', stepString('Wall'));
    return this.wallMaterialRef;
  }

  /** Shared single-layer IfcMaterialLayerSet for `thickness`, named by it (mm). */
  private getWallLayerSet(thickness: number): string {
    const key = thickness.toFixed(6);
    let ref = this.wallLayerSetByThickness.get(key);
    if (!ref) {
      const layer = this.addEntity('IFCMATERIALLAYER', `${this.getWallMaterial()},${stepFloat(thickness)},${STEP_UNDEFINED}`);
      const name = `Wall ${Math.round(thickness * 1000)}mm`;
      ref = this.addEntity('IFCMATERIALLAYERSET', `${stepList([layer])},${stepString(name)}`);
      this.wallLayerSetByThickness.set(key, ref);
    }
    return ref;
  }

  /**
   * Cut each opening from `wall` (void), and for typed openings place a thin
   * IfcDoor/IfcWindow filling. Returns the filling refs (for spatial containment).
   */
  private cutOpenings(wall: string, wallPlacement: string, openings: IfcOpeningVoid[] | undefined, name: string, refs: ProjectRefs): string[] {
    const fillings: string[] = [];
    if (openings) {
      for (let i = 0; i < openings.length; i++) {
        const filling = this.writeOpening(`${name}_opening_${i}`, wall, wallPlacement, openings[i], refs);
        if (filling) fillings.push(filling);
      }
    }
    return fillings;
  }

  /**
   * Cut the opening from `wall` (void), and for a typed opening place a thin
   * IfcDoor/IfcWindow filling. Returns the filling ref (for containment) or
   * null. Skips degenerate openings.
   */
  private writeOpening(fallbackName: string, wall: string, wallPlacement: string, opening: IfcOpeningVoid, refs: ProjectRefs): string | null {
    if (opening.footprint.length < 3 || opening.height <= 0) return null;
    const name = opening.name ?? fallbackName;

    // Opening placement nests under the wall; the filling nests under the opening,
    // so the wall → opening → door/window hosting hierarchy is explicit. All are
    // identity transforms (geometry already carries world-local coords).
    const openingPlacement = this.localPlacement(wallPlacement, refs.identityAxis);

    // Void overshoots the faces and sill/head so the cut fully penetrates.
    const voidShape = this.writeExtrudedProdShape(overshootRect(opening.footprint, VOID_OVERSHOOT), opening.elevation - VOID_OVERSHOOT, opening.height + 2 * VOID_OVERSHOOT, refs);
    const openingElement = this.addEntity('IFCOPENINGELEMENT', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString(name)},${STEP_UNDEFINED},${STEP_UNDEFINED},${openingPlacement},${voidShape},${STEP_UNDEFINED}`);
    this.addEntity('IFCRELVOIDSELEMENT', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${wall},${openingElement}`);

    if (!opening.kind || !EMIT_OPENING_FILLINGS) return null;

    // Thin filling placed back in the hole via IfcRelFillsElement.
    const fillingPlacement = this.localPlacement(openingPlacement, refs.identityAxis);
    const pane = thinPane(opening.footprint, FILLING_THICKNESS);
    const styleRef = opening.kind === 'DOOR' ? this.getDoorStyle() : this.getWindowStyle();
    const fillShape = this.writeExtrudedProdShape(pane.rect, opening.elevation, opening.height, refs, styleRef);
    const dims = `${stepFloat(opening.height)},${stepFloat(pane.width)}`;
    const filling =
      opening.kind === 'DOOR'
        ? this.addEntity('IFCDOOR', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString(name)},${STEP_UNDEFINED},${STEP_UNDEFINED},${fillingPlacement},${fillShape},${STEP_UNDEFINED},${dims}`)
        : this.addEntity('IFCWINDOW', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${stepString(name)},${STEP_UNDEFINED},${STEP_UNDEFINED},${fillingPlacement},${fillShape},${STEP_UNDEFINED},${dims}`);

    this.addEntity('IFCRELFILLSELEMENT', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${openingElement},${filling}`);
    return filling;
  }

  /**
   * Write the spatial containment relationship linking walls to a storey.
   */
  writeSpatialContainment(wallRefs: string[], storeyRef: string): void {
    if (wallRefs.length === 0) return;

    this.addEntity('IFCRELCONTAINEDINSPATIALSTRUCTURE', `${stepString(generateIfcGuid())},${STEP_UNDEFINED},${STEP_UNDEFINED},${STEP_UNDEFINED},${stepList(wallRefs)},${storeyRef}`);
  }

  /**
   * Write the DATA section footer.
   */
  writeFooter(): void {
    this.addLine('ENDSEC;');
    this.addLine('');
    this.addLine('END-ISO-10303-21;');
  }

  /**
   * Return the complete IFC file as a string.
   */
  toString(): string {
    return this.lines.join('\n');
  }
}
